import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Upload, X, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublishJob({ user, token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    budget: '',
    urgency: 'normal'
  });

  const categories = [
    { value: 'plomeria', label: 'Plomería' },
    { value: 'electricidad', label: 'Electricidad' },
    { value: 'pintura', label: 'Pintura' },
    { value: 'albanileria', label: 'Albañilería' },
    { value: 'ingeniero_civil', label: 'Ingeniero Civil' },
    { value: 'arquitecto', label: 'Arquitecto' },
    { value: 'topografia', label: 'Topografía' },
    { value: 'cerrajeria', label: 'Cerrajería' },
    { value: 'planos_disenos', label: 'Planos y Diseños' },
    { value: 'licencias', label: 'Licencias de Construcción' }
  ];

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (images.length + files.length > 3) {
      toast.error('Máximo 3 fotos permitidas');
      return;
    }

    // Convert images to base64
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Cada imagen debe ser menor a 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImages(prev => [...prev, reader.result]);
        setImagePreviews(prev => [...prev, URL.createObjectURL(file)]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Por favor ingresa la actividad');
      return;
    }

    if (!formData.category) {
      toast.error('Por favor selecciona una categoría');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Por favor describe el problema');
      return;
    }

    setLoading(true);

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        title: formData.title,
        category: formData.category,
        description: formData.description,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        urgency: formData.urgency,
        location: { address: 'Bogotá, Colombia' },
        images: images
      };

      const response = await axios.post(`${API}/jobs`, payload, config);
      
      toast.success('¡Trabajo publicado exitosamente!');
      navigate(`/job/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al publicar trabajo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 sticky top-0 z-40">
        <div className="container mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate('/dashboard')}
            className="text-white hover:text-orange-500 mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="h-6 w-6" />
            Volver
          </button>
          <h1 className="text-2xl font-bold" data-testid="page-title">PUBLICAR TRABAJO</h1>
          <p className="text-slate-300 text-sm mt-1">Describe tu necesidad y encuentra al profesional ideal</p>
        </div>
      </div>

      {/* Form */}
      <div className="container mx-auto px-4 py-6">
        <Card data-testid="publish-job-form">
          <CardHeader>
            <CardTitle>Detalles del Trabajo</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Actividad */}
              <div>
                <Label htmlFor="activity">Actividad *</Label>
                <Input
                  id="activity"
                  data-testid="input-activity"
                  placeholder="Ej: Reparación de fuga de agua en baño"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                />
              </div>

              {/* Categoría */}
              <div>
                <Label htmlFor="category">Categoría *</Label>
                <Select value={formData.category} onValueChange={(value) => handleChange('category', value)}>
                  <SelectTrigger data-testid="select-category">
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Descripción */}
              <div>
                <Label htmlFor="description">Descripción del Problema *</Label>
                <Textarea
                  id="description"
                  data-testid="input-description"
                  placeholder="Describe detalladamente el problema, ubicación exacta, qué necesitas..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={5}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Mientras más detalles proporciones, mejor será la respuesta de los trabajadores
                </p>
              </div>

              {/* Fotos */}
              <div>
                <Label>Fotos del Problema (Máximo 3)</Label>
                <div className="mt-2">
                  {imagePreviews.length < 3 && (
                    <label
                      data-testid="upload-images-button"
                      className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-orange-500 hover:bg-orange-50 transition-colors"
                    >
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="h-8 w-8 text-slate-400 mb-2" />
                        <p className="text-sm text-slate-600">
                          <span className="font-semibold">Click para subir</span> o arrastra las fotos
                        </p>
                        <p className="text-xs text-slate-500 mt-1">PNG, JPG hasta 5MB</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                      />
                    </label>
                  )}

                  {/* Image Previews */}
                  {imagePreviews.length > 0 && (
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      {imagePreviews.map((preview, index) => (
                        <div
                          key={index}
                          data-testid={`image-preview-${index}`}
                          className="relative group"
                        >
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-slate-200"
                          />
                          <button
                            type="button"
                            data-testid={`remove-image-${index}`}
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Presupuesto */}
              <div>
                <Label htmlFor="budget">Presupuesto Estimado (COP)</Label>
                <Input
                  id="budget"
                  data-testid="input-budget"
                  type="number"
                  placeholder="Ej: 150000"
                  value={formData.budget}
                  onChange={(e) => handleChange('budget', e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Opcional - Ayuda a los trabajadores a enviar propuestas más precisas
                </p>
              </div>

              {/* Urgencia */}
              <div>
                <Label htmlFor="urgency">Urgencia</Label>
                <Select value={formData.urgency} onValueChange={(value) => handleChange('urgency', value)}>
                  <SelectTrigger data-testid="select-urgency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                    <SelectItem value="emergency">Emergencia</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="submit-job-button"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-6 rounded-full text-lg"
                disabled={loading}
              >
                {loading ? 'Publicando...' : 'Publicar Trabajo'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 bg-orange-50 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <ImageIcon className="h-5 w-5 text-orange-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-orange-900 mb-1">Consejos para mejores resultados</h3>
                <ul className="text-sm text-orange-800 space-y-1">
                  <li>• Incluye fotos claras del problema desde diferentes ángulos</li>
                  <li>• Describe la ubicación exacta dentro de tu propiedad</li>
                  <li>• Menciona si tienes materiales o herramientas disponibles</li>
                  <li>• Indica tu disponibilidad horaria preferida</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
